import {Audit, GetAuditsRequest} from "@/Stub/V1";
import { Column, ColumnBodyOptions } from "primereact/column";
import { DataTable, DataTablePassThroughOptions, DataTableStateEvent } from "primereact/datatable";
import styled from "styled-components";
import React, {useMemo, useState} from "react";
import { snakeToNormalCase, url } from "@/Helpers/general";
import { Button } from "primereact/button";
import { useUpdateSearchParams } from "@/Hooks/useUpdateSearchParams";
import {useGetAudits} from "@/Service/Api/V1/ApiHooks/Audits/useGetAudits";
import {formatToUKDate} from "@/Util/formatToUKDate";
import {Dialog} from "primereact/dialog";
import AuditPreview from "@/Components/Partials/AuditPreview";
import useGetUsersFiltersQuery, {AuditFilterType} from "@/Hooks/useGetAuditsFiltersQuery";
import useGetAuditsFiltersQuery from "@/Hooks/useGetAuditsFiltersQuery";

type TableColumnDefinition = {
    label: string
    sortable?: boolean
    field?: string
    sortField?: string
    headerTooltip?: string
    body?: React.ReactNode | ((data: Audit, options: ColumnBodyOptions) => React.ReactNode)
};

const dataTablePtOptions: DataTablePassThroughOptions = {
    root: {
        className: 'datatable-base'
    }
};

const StyledWrap = styled.main`
    .content-header {
        padding: 1rem;
        border-bottom: var(--gray-300) 1px solid;

        &__title {
            margin: 0.25rem 0;
            font-weight: 600;
            font-size: 1.5rem;
        }

        &__description {
            font-size: 0.9rem;
            color: var(--gray-500);
        }
    }

    .content-container {
        margin-top: 2rem;
        padding: 0 1rem;
    }

    .datatable-container {
        margin-top: 2rem;
        font-size: 0.8rem;
    }

    .datatable-base {
        font-size: 0.8rem;
        font-weight: 500;
    }

    .datatable-cell {
        &__clean-text {
            margin: 0;
        }
    }

    .angles-sort {
        margin-left: 0.3rem;

        &__icon {
            color: var(--primary-200);
            font-size: 0.8rem;
            font-weight: 600;

            &.active {
                color: var(--primary-500);
            }
        }
    }
`;


export type AuditsDatatableProps = {
    requestParams?: Partial<GetAuditsRequest>
};


const AuditsDatatable = ({ requestParams }: AuditsDatatableProps) => {
    const [previewAudit, setPreviewAudit] = useState<Audit|undefined>(undefined);
    const {
        pageQuery,
        pageSizeQuery,
    } = useGetAuditsFiltersQuery();
    const updateAuditsFilterHandler = useUpdateSearchParams();


    const auditsRequest = useMemo<GetAuditsRequest>(() => {
        return {
            ...requestParams,
            page: pageQuery ? parseInt(pageQuery) : 1,
            per_page: pageSizeQuery ? parseInt(pageSizeQuery) : 10,
        };
    }, [requestParams, pageQuery, pageSizeQuery]);

    const {
        data: auditsData,
        isLoading: auditsLoading
    } = useGetAudits({
        requestParams: auditsRequest
    });

    const actionsBodyTemplate = (audit: Audit) => {
        return (
            <div className="flex gap-2" key={`audit-${audit.id}`}>
                <Button
                    onClick={() => {setPreviewAudit(audit)}}
                    label='View'
                />
            </div>
        );
    };

    const tableColumns: TableColumnDefinition[] = [
        {
            label: 'Created At',
            field: 'created_at',
            body: audit => <div>{formatToUKDate(audit.created_at, true)}</div>
        },
        {
            label: 'User',
            field: 'user.name',
        },
        {
            label: 'Claim Reference',
            field: 'claim_ref',
            body: audit => <div>{audit.claim_ref ?? 'N/A'}</div>
        },
        {
            label: 'Type',
            field: 'type',
            body: audit => <div>{snakeToNormalCase(audit.type)}</div>
        },
        {
            label: 'Description',
            field: 'description',
        },
        {
            label: 'Actions',
            body: actionsBodyTemplate
        }
    ];


    const onPagination = (e: DataTableStateEvent) => {
        updateAuditsFilterHandler(AuditFilterType.Page, ((e.first / e.rows) + 1).toString());
        updateAuditsFilterHandler(AuditFilterType.PageSize, e.rows.toString());
    };

    const first = pageSizeQuery && pageQuery ? Number(pageSizeQuery) * Number(pageQuery) - 10 : 0;
    const rows = pageSizeQuery ? Number(pageSizeQuery) : 10;

    return (
        <StyledWrap>
            <div className="content-container">
                <DataTable
                    lazy
                    loading={auditsLoading}
                    emptyMessage="No audits found."
                    value={auditsData?.data}
                    totalRecords={auditsData?.meta.total}
                    paginator
                    rows={rows}
                    rowsPerPageOptions={[10, 20, 50]}
                    first={first}
                    pt={dataTablePtOptions}
                    onPage={e => {
                        onPagination(e);
                    }}
                >
                    {tableColumns.map((column, index) =>
                        <Column
                            key={`audits-column-${index}`}
                            field={column.field}
                            body={column.body}
                            header={column.label}
                            sortable={column.sortable}
                            sortField={column.sortField}
                            headerTooltip={column.headerTooltip}
                        />
                    )}
                </DataTable>
                <Dialog header="Information about Audit" draggable={false} resizable={false} visible={!!previewAudit}
                        style={{ width: '50vw' }} onHide={() => {
                    if (!previewAudit) return;
                    setPreviewAudit(undefined);
                }}>
                    <AuditPreview audit={previewAudit}/>
                </Dialog>
            </div>
        </StyledWrap>
    );
};

export default AuditsDatatable;
